import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = ["onMousedown"]

import { computed, onBeforeUnmount, ref, onMounted, watch, nextTick } from "vue";
import type { LiteColumn } from "../types";


export default /*@__PURE__*/_defineComponent({
  __name: 'QTLiteResizeOverlay',
  props: {
    columns: {},
    tableEl: {},
    scrollEl: {},
    topOffset: {}
  },
  emits: ["resizeStart", "resize", "resizeEnd"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// чтобы подсветить полоски и увидеть их при отладке, ставим window.__qtlDebugResize = true
const debug = ref(false);
function readDebugFlag() {
  if (typeof window !== "undefined") debug.value = !!(window as any).__qtlDebugResize;
}

const rootEl = ref<HTMLElement | null>(null);
const scrollLeft = ref(0);

/*
 Границы колонок берём как правый край каждой ячейки шапки. Это координата
 внутри таблицы, а на экране её сдвигаем на величину прокрутки вбок (смотри
 barsStyle ниже).
*/
const boundaries = ref<{ colId: string; neighborId: string; left: number }[]>([]);

function recomputeBoundaries() {
  const table = props.tableEl;
  if (!table) {
    boundaries.value = [];
    return;
  }
  const ths = Array.from(table.querySelectorAll<HTMLElement>("thead th[data-col-id]"));
  if (!ths.length) {
    boundaries.value = [];
    return;
  }
  const byId = new Map<string, HTMLElement>();
  for (const th of ths) byId.set(th.dataset.colId || "", th);

  const out: { colId: string; neighborId: string; left: number }[] = [];
  for (let i = 0; i < props.columns.length - 1; i++) {
    const col = props.columns[i];
    const next = props.columns[i + 1];
    // первую колонку с чекбоксами не трогаем, у неё ширина жёсткая
    if (col.isMultiedit) continue;
    const th = byId.get(col.id);
    if (!th) continue;
    out.push({ colId: col.id, neighborId: next.id, left: th.offsetLeft + th.offsetWidth });
  }
  boundaries.value = out;
}

const bars = computed(() => boundaries.value);

const barsStyle = computed(() => ({
  // сдвигаем полоски на величину прокрутки вбок, чтобы они стояли ровно на стыках колонок
  transform: `translateX(${-scrollLeft.value}px)`,
  // верх считаем от верхней границы таблицы (под формулами), а не от верха слоя
  top: `${props.topOffset ?? 0}px`,
}));

/*
 Подписываемся на прокрутку и следим за тем, как меняется размер таблицы.
*/
let attachedScroll: HTMLElement | null = null;
let onScroll: (() => void) | null = null;
let tableRO: ResizeObserver | null = null;

function attachScroll(el: HTMLElement | null | undefined) {
  if (attachedScroll === el) return;
  if (attachedScroll && onScroll) attachedScroll.removeEventListener("scroll", onScroll);
  attachedScroll = el || null;
  if (!el) return;
  scrollLeft.value = el.scrollLeft || 0;
  onScroll = () => { scrollLeft.value = el.scrollLeft || 0; };
  el.addEventListener("scroll", onScroll, { passive: true });
}

function attachTableObserver(table: HTMLTableElement | null | undefined) {
  tableRO?.disconnect();
  tableRO = null;
  if (!table) return;
  tableRO = new ResizeObserver(() => recomputeBoundaries());
  tableRO.observe(table);
  // за шапкой тоже следим, потому что при смене ширины колонок двигаются именно её границы
  const thead = table.querySelector("thead");
  if (thead) tableRO.observe(thead);
  // и за каждой ячейкой шапки отдельно: при парном ресайзе (одна колонка растёт,
  // соседняя ужимается) общая ширина таблицы и шапки не меняется, меняются только
  // ширины самих th - без этого границы не пересчитывались бы и полоска оставалась
  // на старом месте.
  table.querySelectorAll<HTMLElement>("thead th[data-col-id]").forEach((th) => tableRO!.observe(th));
  recomputeBoundaries();
}

onMounted(() => {
  readDebugFlag();
  if (typeof window !== "undefined") window.addEventListener("storage", readDebugFlag);
  attachScroll(props.scrollEl);
  attachTableObserver(props.tableEl);
});

watch(() => props.scrollEl, (el) => attachScroll(el ?? null));
watch(() => props.tableEl, (el) => attachTableObserver(el ?? null));
// при смене набора колонок переподключаем наблюдатель к новым ячейкам шапки
watch(() => props.columns, async () => { await nextTick(); attachTableObserver(props.tableEl); }, { deep: false });

/*
 Само перетаскивание границы колонки. Шлём сдвиг от точки, где нажали (а не от
 прошлого движения), чтобы ширину можно было считать от исходной. Тогда, если
 перетянуть дальше, чем колонка может ужаться, она остановится, а при движении
 назад тронется не сразу, а только когда курсор дойдёт до настоящей границы.
 Ширину меняет родитель прямо в DOM, поэтому колонка идёт за курсором без задержки.
*/
let dragColId: string | null = null;
let dragNeighborId: string | undefined;
let dragOriginX = 0;

function onStart(ev: MouseEvent, bar: { colId: string; neighborId: string }) {
  dragColId = bar.colId;
  dragNeighborId = bar.neighborId;
  dragOriginX = ev.clientX;
  // подсветку строки под курсором держим и во время таскания (обновляем в onMove)
  setHoveredRow(rowUnderPoint(ev.clientY));
  document.body.style.cursor = "col-resize";
  document.body.style.userSelect = "none";
  emit("resizeStart", { colId: bar.colId, neighborId: bar.neighborId });
  window.addEventListener("mousemove", onMove);
  window.addEventListener("mouseup", onEnd, { once: true });
}

/*
 Полоски лежат поверх строк и перехватывают события, поэтому под ними ломается
 штатное поведение: строка не получает :hover, а колесо не находит прокручиваемого
 предка (слой ресайза лежит вне контейнера прокрутки). Пробрасываем оба события руками.
*/

// строка, которой мы вручную выставили подсветку, пока курсор над полоской
let hoveredRow: HTMLElement | null = null;

function setHoveredRow(tr: HTMLElement | null) {
  if (hoveredRow === tr) return;
  if (hoveredRow) hoveredRow.classList.remove("qtl-row--hover");
  hoveredRow = tr;
  if (hoveredRow) hoveredRow.classList.add("qtl-row--hover");
}

function rowUnderPoint(y: number): HTMLElement | null {
  // ищем строку данных по вертикальной координате курсора среди отрисованных <tr>
  // (у распорок виртуализатора класс qtl-vpad, они сюда не попадают). Через
  // getBoundingClientRect, а не elementsFromPoint: полоска перекрывает точку, а так
  // попадание в строку определяется однозначно.
  const table = props.tableEl;
  if (!table) return null;
  const trs = table.querySelectorAll<HTMLElement>("tbody tr.qtl-row");
  for (const tr of trs) {
    const r = tr.getBoundingClientRect();
    if (y >= r.top && y < r.bottom) return tr;
  }
  return null;
}

function onBarMouseMove(ev: MouseEvent) {
  if (dragColId) return;
  setHoveredRow(rowUnderPoint(ev.clientY));
}

function onBarMouseLeave() {
  setHoveredRow(null);
}

function onWheel(ev: WheelEvent) {
  const el = props.scrollEl;
  if (!el) return;
  // deltaMode: 0 - пиксели, 1 - строки, 2 - страницы; приводим к пикселям
  const factor = ev.deltaMode === 1 ? 16 : ev.deltaMode === 2 ? el.clientHeight : 1;
  el.scrollTop += ev.deltaY * factor;
  el.scrollLeft += ev.deltaX * factor;
  ev.preventDefault();
}

function onMove(ev: MouseEvent) {
  if (!dragColId) return;
  const delta = ev.clientX - dragOriginX;
  emit("resize", { colId: dragColId, neighborId: dragNeighborId, delta });
  // во время таскания курсор по вертикали может гулять по другим строкам -
  // подсвечиваем ту, что под ним, чтобы подсветка не пропадала
  setHoveredRow(rowUnderPoint(ev.clientY));
}

function onEnd(ev: MouseEvent) {
  window.removeEventListener("mousemove", onMove);
  dragColId = null;
  dragNeighborId = undefined;
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
  emit("resizeEnd");
  // после отпускания: если курсор всё ещё над полоской - подсветку строки оставляем
  // (штатный :hover под полоской не работает), иначе снимаем и отдаём управление CSS
  const overBar = !!(document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null)
    ?.classList?.contains("qtl-resize-bar");
  setHoveredRow(overBar ? rowUnderPoint(ev.clientY) : null);
}

onBeforeUnmount(() => {
  setHoveredRow(null);
  window.removeEventListener("mousemove", onMove);
  if (attachedScroll && onScroll) attachedScroll.removeEventListener("scroll", onScroll);
  tableRO?.disconnect();
  tableRO = null;
  attachedScroll = null;
  onScroll = null;
  if (typeof window !== "undefined") window.removeEventListener("storage", readDebugFlag);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "rootEl",
    ref: rootEl,
    class: _normalizeClass(["qtl-resize-overlay", { 'qtl-resize-overlay--debug': debug.value }])
  }, [
    _createElementVNode("div", {
      class: "qtl-resize-bars",
      style: _normalizeStyle(barsStyle.value)
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bars.value, (bar) => {
        return (_openBlock(), _createElementBlock("span", {
          key: bar.colId,
          class: _normalizeClass(["qtl-resize-bar", { 'qtl-resize-bar--debug': debug.value }]),
          style: _normalizeStyle({ left: bar.left + 'px' }),
          onMousedown: _withModifiers(($event: any) => (onStart($event, bar)), ["stop","prevent"]),
          onMousemove: onBarMouseMove,
          onMouseleave: onBarMouseLeave,
          onWheel: onWheel
        }, null, 46, _hoisted_1))
      }), 128))
    ], 4)
  ], 2))
}
}

})